/**
 * Configuration de l'API et client Axios
 */

import axios from 'axios';

// Configuration de base
export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// Créer l'instance Axios
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Intercepteur pour ajouter le token, l'année académique et gérer le Content-Type
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token') || localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Année académique - envoyée dans chaque requête pour le schéma PostgreSQL
    // Un en-tête posé explicitement par l'appelant (consultation d'une année
    // archivée) prime sur l'année globale du localStorage.
    const anneeAcademique = localStorage.getItem('annee_academique');
    if (anneeAcademique && !config.headers['X-Annee-Academique']) {
      config.headers['X-Annee-Academique'] = anneeAcademique;
    }
    // Laisser axios définir automatiquement le Content-Type pour FormData
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// ── Gestion 401 avec refresh JWT + garde-fous anti-boucle ──────────
// Alignée sur baseApi.ts (RTK Query) pour éviter que deux systèmes d'auth
// se comportent différemment (le hard-reload vers /login pendant la
// frappe d'un utilisateur = « la page recharge toute seule »).
let _refreshInFlight: Promise<string | null> | null = null;

async function _tenterRefreshJwt(): Promise<string | null> {
  const refresh = localStorage.getItem('refresh_token');
  if (!refresh) return null;
  // Fetch direct (sans re-entrer dans apiClient — évite boucle infinie)
  try {
    const res = await fetch(`${API_BASE_URL}/auth/token/refresh/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh }),
    });
    if (!res.ok) return null;
    const data = await res.json();
    if (data?.access) {
      localStorage.setItem('auth_token', data.access);
      localStorage.setItem('access_token', data.access);
      return data.access as string;
    }
  } catch { /* ignore */ }
  return null;
}

function _redirigerLoginSiNecessaire() {
  // Ne PAS rediriger si l'utilisateur est sur le portail étudiant
  // (auth différente) ni s'il est déjà sur /login (évite boucle).
  const path = window.location.pathname;
  // `/portail` ou `/portail/…` seulement : `/portail-admin` est une page du back-office.
  if (path === '/login' || path === '/portail' || path.startsWith('/portail/')) return;
  localStorage.removeItem('auth_token');
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user_data');
  window.location.href = '/login';
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalReq = error.config || {};
    if (error.response?.status !== 401 || originalReq._retry) {
      return Promise.reject(error);
    }
    // Ne pas essayer le refresh sur l'endpoint /auth/* lui-même
    const url: string = String(originalReq.url || '');
    if (url.includes('/auth/token/refresh/') || url.includes('/auth/login/')) {
      _redirigerLoginSiNecessaire();
      return Promise.reject(error);
    }
    // Un seul refresh en vol partagé par toutes les requêtes 401 concurrentes
    if (!_refreshInFlight) {
      _refreshInFlight = _tenterRefreshJwt().finally(() => { _refreshInFlight = null; });
    }
    const newToken = await _refreshInFlight;
    if (!newToken) {
      _redirigerLoginSiNecessaire();
      return Promise.reject(error);
    }
    // Rejouer la requête d'origine avec le nouveau token
    originalReq._retry = true;
    originalReq.headers = originalReq.headers || {};
    originalReq.headers.Authorization = `Bearer ${newToken}`;
    return apiClient(originalReq);
  }
);

export default apiClient;
